import { TimelineItem } from '@ps/isp-web-component-library';
import { Ninety } from '@ps/isp-web-component-library/icons/Ninety';
import { StarSubIcon } from '@ps/isp-web-component-library/icons/StarSubIcon';
import { TwoUpIcon } from '@ps/isp-web-component-library/icons/TwoUpIcon';
import type { EventBasedMarketView, MarketId, SelectionId } from '@ps/isp-web-services-types';
import { getIsMatchOddsNinetyMarket, getIsTwoUpMarket } from '@ps/web-sports-utils';
import React from 'react';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import { Selection } from '@swf/sports/components';
import FormattedDateTime from '@swf/sports/components/FormattedDateTime/FormattedDateTime';
import { useNormalisedEventBasedData } from '@swf/sports/hooks/useNormalisedEventBasedData';
import { getHandicapSelectionValue } from '@swf/sports/utils/getHandicapSelectionValue';

import {
  TXTWCL_Match_Odds_90,
  TXTWCL_Match_Odds___2_Up,
  TXTWCL_Star_Sub_enabled_selection
} from './EventBasedPopularAccumulatorItem.messages.json';
import styles from './EventBasedPopularAccumulatorItem.scss';

interface Props {
  marketId: MarketId;
  selectionId: SelectionId;
}

const EventBasedPopularAccumulatorItem: React.FC<Props> = ({ marketId, selectionId }: Props) => {
  const { market, event } = useNormalisedEventBasedData({ marketId });
  const { formatMessage } = useRegulatoryIntl();

  const runner = market?.runners.find((runner) => runner.selectionId === selectionId);
  const isMatchOddsNinetyMarket = getIsMatchOddsNinetyMarket(market?.marketType);
  const isTwoUpMarket = getIsTwoUpMarket(market?.marketType);

  if (!market || !runner) return null;

  const { runnerName, handicap, resultType } = runner;
  const handicapValue = getHandicapSelectionValue({
    handicap,
    runnerResultType: resultType
  });

  const marketToIconConfigMap = {
    starSub: {
      icon: StarSubIcon,
      label: formatMessage(TXTWCL_Star_Sub_enabled_selection)
    },
    matchOddsNinety: {
      icon: Ninety,
      label: formatMessage(TXTWCL_Match_Odds_90)
    },
    twoUp: {
      icon: TwoUpIcon,
      label: formatMessage(TXTWCL_Match_Odds___2_Up)
    }
  };

  const getMarketIcon = (market: EventBasedMarketView) => {
    if (market.isStarSubEnabled) return marketToIconConfigMap.starSub;
    if (isMatchOddsNinetyMarket) return marketToIconConfigMap.matchOddsNinety;
    if (isTwoUpMarket) return marketToIconConfigMap.twoUp;
    return undefined;
  };

  const iconConfig = getMarketIcon(market);

  return (
    <TimelineItem
      textWrap="balance"
      icon={iconConfig?.icon}
      iconSize="sm"
      iconLabel={iconConfig?.label}
    >
      <div className={styles.item}>
        <div className={styles.singleDetails}>
          <span>
            <strong>
              {runnerName}
              {handicapValue ? ` (${handicapValue})` : null}
            </strong>{' '}
            - {market.marketName}
          </span>
          <span>{event.eventName}</span>
          <span className={styles.singleDetailsDate}>
            <FormattedDateTime date={event.eventStartTime} format="long" minimal />
          </span>
        </div>
        <span className={styles.selection}>
          <Selection
            competitionId={market.competitionId}
            eventId={market.eventId}
            eventTypeId={market.eventTypeId}
            handicap={runner.handicap}
            isStartingPriceMarket={market.isStartingPriceMarket}
            key={runner.selectionId}
            legTypes={market.legTypes}
            marketId={market.marketId}
            marketStatus={market.marketStatus}
            marketType={market.marketType}
            odds={runner.winRunnerOdds}
            runnerResultType={runner.resultType}
            runnerStatus={runner.runnerStatus}
            selectionId={runner.selectionId}
            startTime={market.marketTime}
          />
        </span>
      </div>
    </TimelineItem>
  );
};

export default EventBasedPopularAccumulatorItem;
